import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { type AuthStatus, AuthContext } from './useAuth'
import { attemptRefresh } from '../lib/authRefresh'

const _SESSION_KEY = 'studio_session'
const _PROFILE_KEY = 'studio_profile'
const _CBE_URL_KEY = 'studio_cbe_url'
const _REASON_KEY = 'studio_auth_reason'

// Migrate legacy storage keys on first load
;(function migrateStorageKeys() {
  // Migrate old key name within localStorage
  const lsOldKey = 'spark_studio_cbe_url'
  const v = localStorage.getItem(lsOldKey)
  if (v !== null && localStorage.getItem(_CBE_URL_KEY) === null) {
    localStorage.setItem(_CBE_URL_KEY, v)
    localStorage.removeItem(lsOldKey)
  }
  // Migrate session flag and profile from sessionStorage → localStorage (shared across tabs)
  for (const key of [_SESSION_KEY, _PROFILE_KEY]) {
    const sv = sessionStorage.getItem(key)
    if (sv !== null && localStorage.getItem(key) === null) {
      localStorage.setItem(key, sv)
    }
    sessionStorage.removeItem(key)
  }
})()

interface Profile {
  displayName: string | null
  email: string | null
  tenantUrl: string | null
  tenantId: string | null
  isGlobalAdmin: boolean
  baseStudioUrl: string | null
  agentsContracted: boolean
  studioRole: 'Admin' | 'Editor' | null
  /** True only while the cached profile predates `studioRole` (written before
   * MS-741 shipped), so `studioRole: null` there means "not recorded yet", not
   * "no Studio role". Route guards must hold rather than deny while it is set —
   * see lib/roleAccess.ts. Cleared by the first server response. */
  studioRolePending: boolean
  // MS-866 AC6 — tri-state entitlements signal (R-ENT-2/R-ENT-3). False means the
  // entitlements lookup itself failed (timeout/5xx/malformed) — distinct from a
  // failure of /auth/session as a whole, which stays fail-open (see the two
  // `.catch()` blocks below, deliberately untouched).
  entitlementsAvailable: boolean
  /** True only while the cached profile predates `entitlementsAvailable`
   * (written before MS-866 shipped) — mirrors `studioRolePending` exactly, so a
   * pre-existing session isn't misread as degraded on the first paint after
   * deploy. Every consumer must honour this, not just the route guard — see
   * AppShell.tsx / TopBar.tsx / Home.tsx / RequireEntitlements. Cleared by the
   * first server response. */
  entitlementsPending: boolean
}

function loadProfile(): Profile {
  const lsTenantUrl = localStorage.getItem(_CBE_URL_KEY)
  try {
    const raw = localStorage.getItem(_PROFILE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Profile>
      return {
        displayName: parsed.displayName ?? null,
        email: parsed.email ?? null,
        tenantUrl: parsed.tenantUrl ?? lsTenantUrl,
        tenantId: parsed.tenantId ?? null,
        isGlobalAdmin: parsed.isGlobalAdmin ?? false,
        baseStudioUrl: parsed.baseStudioUrl ?? null,
        agentsContracted: parsed.agentsContracted ?? true,
        studioRole: parsed.studioRole ?? null,
        studioRolePending: !('studioRole' in parsed),
        entitlementsAvailable: parsed.entitlementsAvailable ?? true,
        entitlementsPending: !('entitlementsAvailable' in parsed),
      }
    }
  } catch { /* ignore */ }
  return { displayName: null, email: null, tenantUrl: lsTenantUrl, tenantId: null, isGlobalAdmin: false, baseStudioUrl: null, agentsContracted: true, studioRole: null, studioRolePending: false, entitlementsAvailable: true, entitlementsPending: false }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() =>
    localStorage.getItem(_SESSION_KEY) === '1' ? 'authenticated' : 'checking'
  )
  const [didLogOut, setDidLogOut] = useState(false)
  const [didExpire, setDidExpire] = useState(false)
  const [didAuthFailed, setDidAuthFailed] = useState(false)
  const [profile, setProfileState] = useState(loadProfile)
  const statusRef = useRef(status)
  statusRef.current = status

  const setAuth = useCallback(
    (
      authenticated: boolean,
      newProfile?: {
        displayName?: string | null
        email?: string | null
        tenantUrl?: string | null
        tenantId?: string | null
        isGlobalAdmin?: boolean
        baseStudioUrl?: string | null
        agentsContracted?: boolean
        studioRole?: 'Admin' | 'Editor' | null
        entitlementsAvailable?: boolean
      },
      reason?: 'expired' | 'logout' | 'authFailed'
    ) => {
      if (authenticated) {
        if (localStorage.getItem(_SESSION_KEY) !== '1') {
          localStorage.setItem(_SESSION_KEY, '1')
        }
        localStorage.removeItem(_REASON_KEY)
        setDidLogOut(false)
        setDidExpire(false)
        setDidAuthFailed(false)
        if (newProfile) {
          const merged: Profile = {
            displayName: newProfile.displayName ?? null,
            email: newProfile.email ?? null,
            tenantUrl: newProfile.tenantUrl ?? null,
            tenantId: newProfile.tenantId ?? null,
            isGlobalAdmin: newProfile.isGlobalAdmin ?? false,
            baseStudioUrl: newProfile.baseStudioUrl ?? null,
            agentsContracted: newProfile.agentsContracted ?? true,
            studioRole: newProfile.studioRole ?? null,
            // Came from /auth/session (or /exchange): `null` here is a real
            // "no Studio role", not an absent field.
            studioRolePending: false,
            entitlementsAvailable: newProfile.entitlementsAvailable ?? true,
            entitlementsPending: false,
          }
          const serialized = JSON.stringify(merged)
          if (localStorage.getItem(_PROFILE_KEY) !== serialized) {
            localStorage.setItem(_PROFILE_KEY, serialized)
          }
          setProfileState(merged)
          if (newProfile.tenantUrl) {
            localStorage.setItem(_CBE_URL_KEY, newProfile.tenantUrl)
          }
        }
        setStatus('authenticated')
      } else {
        localStorage.setItem(_REASON_KEY, reason ?? 'logout')
        localStorage.removeItem(_SESSION_KEY)
        localStorage.removeItem(_PROFILE_KEY)
        setProfileState({ displayName: null, email: null, tenantUrl: null, tenantId: null, isGlobalAdmin: false, baseStudioUrl: null, agentsContracted: true, studioRole: null, studioRolePending: false, entitlementsAvailable: true, entitlementsPending: false })
        setDidExpire(reason === 'expired')
        setDidAuthFailed(reason === 'authFailed')
        setDidLogOut(reason !== 'expired' && reason !== 'authFailed')
        setStatus('unauthenticated')
      }
    },
    []
  )

  // Always-on mount probe — discovers a valid cookie in new tabs without a session flag.
  // Skipped on auth-handoff routes where OAuthCallback drives setAuth directly, preventing
  // a race where the probe's 401 (no cookie yet) arrives after the exchange sets the cookie.
  useEffect(() => {
    const p = window.location.pathname
    if (p === '/oauth2/callback' || p === '/access-denied') return
    const hadFlag = localStorage.getItem(_SESSION_KEY) === '1'
    let cancelled = false

    async function probeSession(): Promise<Response> {
      return fetch('/api/v1/auth/session', { credentials: 'include' })
    }

    probeSession()
      .then(async (r) => {
        if (cancelled) return
        if (r.ok) {
          try {
            const data = (await r.json()) as Partial<Profile>
            if (!cancelled) setAuth(true, data)
          } catch {
            if (!hadFlag) setStatus('unauthenticated')
          }
        } else if (r.status === 401 || r.status === 403) {
          // Only worth attempting a refresh if there was a prior session — nothing
          // to refresh otherwise.
          if (hadFlag) {
            const refreshed = await attemptRefresh()
            if (cancelled) return
            if (refreshed) {
              const retryResp = await probeSession()
              if (cancelled) return
              if (retryResp.ok) {
                try {
                  const data = (await retryResp.json()) as Partial<Profile>
                  if (!cancelled) setAuth(true, data)
                  return
                } catch {
                  // fall through to unauthenticated below
                }
              }
            }
            setAuth(false, undefined, 'expired')
          } else {
            localStorage.removeItem(_REASON_KEY)
            setStatus('unauthenticated')
          }
        } else if (!hadFlag) {
          setStatus('unauthenticated')
        }
      })
      .catch(() => {
        if (!cancelled && !hadFlag) setStatus('unauthenticated')
      })
    return () => { cancelled = true }
  }, [setAuth])

  // Re-validate the cached profile when the tab regains focus. Without this, a
  // permission-level change made elsewhere (e.g. an admin downgrading this user
  // in Global Settings → Access) would not be reflected in an already-open tab
  // until it happens to reload or hit a 401 — profile fields like isGlobalAdmin
  // are otherwise only refreshed at mount, explicit login, and token refresh.
  useEffect(() => {
    function revalidateOnFocus() {
      if (document.visibilityState !== 'visible') return
      if (statusRef.current !== 'authenticated') return
      fetch('/api/v1/auth/session', { credentials: 'include' })
        .then(async (r) => {
          if (!r.ok) return
          try {
            const data = (await r.json()) as Partial<Profile>
            setAuth(true, data)
          } catch {
            // Malformed response — keep the existing cached profile rather than guessing.
          }
        })
        .catch(() => {
          // Network error — keep the existing cached profile; the next successful
          // focus/mount/refresh will catch up.
        })
    }
    document.addEventListener('visibilitychange', revalidateOnFocus)
    window.addEventListener('focus', revalidateOnFocus)
    return () => {
      document.removeEventListener('visibilitychange', revalidateOnFocus)
      window.removeEventListener('focus', revalidateOnFocus)
    }
  }, [setAuth])

  // Handle session expiry dispatched by lib/api.ts on 401 auth.unauthorized responses
  useEffect(() => {
    function handleSessionExpired() {
      if (statusRef.current !== 'authenticated') return
      setAuth(false, undefined, 'expired')
    }
    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired)
  }, [setAuth])

  // Cross-tab sync — storage events fire in other tabs when this tab writes localStorage
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === _SESSION_KEY) {
        if (e.newValue === null) {
          const storedReason = localStorage.getItem(_REASON_KEY)
          const reason = storedReason === 'expired' || storedReason === 'authFailed' ? storedReason : 'logout'
          setDidExpire(reason === 'expired')
          setDidAuthFailed(reason === 'authFailed')
          setDidLogOut(reason === 'logout')
          setProfileState({ displayName: null, email: null, tenantUrl: null, tenantId: null, isGlobalAdmin: false, baseStudioUrl: null, agentsContracted: true, studioRole: null, studioRolePending: false, entitlementsAvailable: true, entitlementsPending: false })
          setStatus('unauthenticated')
        } else if (e.newValue === '1') {
          setProfileState(loadProfile())
          setDidLogOut(false)
          setDidExpire(false)
          setDidAuthFailed(false)
          setStatus('authenticated')
        }
      } else if (e.key === _PROFILE_KEY) {
        setProfileState(loadProfile())
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const contextValue = useMemo(
    () => ({
      isAuthenticated: status === 'authenticated',
      status,
      setAuth,
      userEmail: profile.email,
      userName: profile.displayName,
      tenantUrl: profile.tenantUrl,
      tenantId: profile.tenantId,
      isGlobalAdmin: profile.isGlobalAdmin,
      baseStudioUrl: profile.baseStudioUrl,
      agentsContracted: profile.agentsContracted,
      studioRole: profile.studioRole,
      studioRolePending: profile.studioRolePending,
      entitlementsAvailable: profile.entitlementsAvailable,
      entitlementsPending: profile.entitlementsPending,
      didLogOut,
      didExpire,
      didAuthFailed,
    }),
    [status, setAuth, profile, didLogOut, didExpire, didAuthFailed]
  )

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
